import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { LoaderService } from 'src/shared/services';
import { ImportTemplateType } from 'src/shared/enums';
import { OverlayType } from 'src/shared/enums/overlay-type.enum';
import { TableFieldService, ZoningCodeService } from 'src/app/core/services';
import { IZoningCodeModel } from 'src/app/interfaces/zoning-code-model.interface';
import { ImportTableWorkflowBaseComponent } from '../shared/import-table-workflow.base';
import { IZCValidationTableResult } from 'src/app/interfaces/zc-validation-result.interface';
import _ from 'lodash';
import ICalibrationSetTableFieldAttribute = ZoneIQCalibration.ICalibrationSetTableFieldAttribute;
import ICalibrationSetTableFieldAttributeImportValidationResult = ZoneIQCalibration.ICalibrationSetTableFieldAttributeImportValidationResult;
import IEntityTableFieldAttributeImportValidationResult = ZoneIQCalibration.IEntityTableFieldAttributeImportValidationResult;
import IImportUnknownEntityData = ZoneIQCalibration.IImportUnknownEntityData;
import ICalibrationSetTableFieldAttributesImportTableRow = ZoneIQCalibration.ICalibrationSetTableFieldAttributesImportTableRow;
import ITableField = ZoneIQCalibration.ITableField;

interface ICalibrationSetTableFieldAttributesValidationZoningCodeModel extends IZoningCodeModel {
    messages?: IZCValidationTableResult[];
}

interface ICalibrationSetTableFieldAttributeImportDisplayRow {
    entityType: string;
    entityName: string;
    dbFieldName: string;
    assumption: string;
    description: string;
    entityImportValidationResult: IEntityTableFieldAttributeImportValidationResult;
    tableFieldValidationResult?: ICalibrationSetTableFieldAttributeImportValidationResult;
    assumptionImportValidationResult?: ICalibrationSetTableFieldAttributeImportValidationResult;
    descriptionImportValidationResult?: ICalibrationSetTableFieldAttributeImportValidationResult;
}

@Component({
    standalone: false,
    selector: 'app-calibration-set-table-field-attributes-import-table',
    templateUrl: './calibration-set-table-field-attributes-import-table.component.html',
    styleUrls: ['./calibration-set-table-field-attributes-import-table.component.scss']
})
export class CalibrationSetTableFieldAttributesImportTableComponent
    extends ImportTableWorkflowBaseComponent<ICalibrationSetTableFieldAttributesValidationZoningCodeModel>
    implements OnInit {
    zoningCode: IZoningCodeModel = null;
    readonly ImportTemplateType = ImportTemplateType;

    readonly validationData$ = new BehaviorSubject<IZoningCodeModel | null>(null);
    private validationData: IZoningCodeModel | null = null;

    constructor(
        private readonly tableFieldService: TableFieldService,
        private readonly zoningCodeService: ZoningCodeService,
        loaderService: LoaderService,
        dialog: MatDialog,
        toastrService: ToastrService
    ) {
        super(loaderService, dialog, toastrService);
    }

    ngOnInit(): void {
        this.loaderService.show();
        this.zoningCodeService.zoningCodes.pipe(filter(zc => zc !== null && zc.length > 0)).subscribe(zc => {
            this.zoningCode = zc[0];
            this.loaderService.hide();
        });
    }

    clearTable(): void {
        this.selectedFileData = null;
        this.resetValidationData();
    }

    protected getValidationData(): IZoningCodeModel {
        return this.validationData;
    }

    protected resetValidationData(): void {
        this.validationData = null;
        this.validationData$.next(null);
    }

    protected setValidationData(result: IZoningCodeModel): void {
        this.validationData = result;
        this.validationData$.next(result);
    }

    protected getImportConfirmationMessage(): string {
        return 'Calibration Set Table Field Attributes will be imported to the selected Zoning Code. Continue?';
    }

    protected runValidationRequest(): Observable<ICalibrationSetTableFieldAttributesValidationZoningCodeModel> {
        return this.zoningCodeService.validateCalibrationSetTableFieldAttributes(
            this.zoningCode.Id,
            this.selectedFileData
        );
    }

    protected runImportRequest(): Observable<ICalibrationSetTableFieldAttributesValidationZoningCodeModel> {
        return this.zoningCodeService.importCalibrationSetTableFieldAttributes(
            this.zoningCode.Id,
            this.selectedFileData
        );
    }

    get validationRows(): ICalibrationSetTableFieldAttributeImportDisplayRow[] {
        if (!this.validationData) {
            return [];
        }

        const rows = _.get(
            this.validationData,
            'importValidationResult.calibrationSetTableFieldAttributesImport.rows',
            []
        ) as ICalibrationSetTableFieldAttributesImportTableRow[];

        return this.getIdentifierBasedValidationRows(rows);
    }

    private getIdentifierBasedValidationRows(
        rows: ICalibrationSetTableFieldAttributesImportTableRow[]
    ): ICalibrationSetTableFieldAttributeImportDisplayRow[] {
        if (_.isEmpty(rows)) {
            return [];
        }
        const zoneCombinations = _.get(this.validationData, 'ZoneCombinations', []) as any[];
        const overlays = _.get(this.validationData, 'Overlays', []) as any[];
        const unknownEntities = _.get(
            this.validationData,
            'importValidationResult.calibrationSetTableFieldAttributesImport.unknownEntities',
            []
        ) as IImportUnknownEntityData[];
        const unknownTableFieldsById = this.getUnknownTableFieldsById();
        const attributes = _.get(
            this.validationData,
            'CalibrationSetTableFieldsAttributes',
            []
        ) as ICalibrationSetTableFieldAttribute[];

        const zonesById = this.groupByLookupKey(zoneCombinations);
        const overlaysById = this.groupByLookupKey(overlays);
        const unknownEntitiesById = this.groupByLookupKey(unknownEntities);

        const attributesById = _.keyBy(attributes, attribute => String(_.get(attribute, 'Id', '')));

        const displayRows = rows.map(row => {
            const entity = this.takeEntityByIdentifiers(row, zonesById, overlaysById, unknownEntitiesById);

            const assumptionAttribute = this.takeAttributeById(attributesById, row.assumptionId);
            const descriptionAttribute = this.takeAttributeById(attributesById, row.descriptionId);

            let tableField = this.tableFieldService.getById(row.tableFieldId);
            if (!tableField) {
                tableField = unknownTableFieldsById[row.tableFieldId] as any;
            }

            return {
                entityType: this.resolveEntityType(row, entity),
                entityName: _.get(entity, 'Name', ''),
                dbFieldName: _.get(tableField, 'Field', ''),
                assumption: _.get(assumptionAttribute, 'AttributeValue', ''),
                description: _.get(descriptionAttribute, 'AttributeValue', ''),
                entityImportValidationResult: _.get(entity, 'importValidationResult', null),
                tableFieldValidationResult: _.get(tableField, 'importValidationResult', null),
                assumptionImportValidationResult: _.get(assumptionAttribute, 'importValidationResult', null),
                descriptionImportValidationResult: _.get(descriptionAttribute, 'importValidationResult', null)
            };
        });


        return displayRows;
    }

    private groupByLookupKey<T>(items: T[]): { [key: string]: T } {
        return (items || []).reduce((acc, item) => {
            const key = _.get(item, 'Id', null);
            if (!acc[key]) {
                acc[key] = item;
            }
            return acc;
        }, {} as { [key: string]: T | null });
    }

    private takeEntityByIdentifiers(
        row: ICalibrationSetTableFieldAttributesImportTableRow,
        zonesById: { [key: string]: any },
        overlaysById: { [key: string]: any },
        unknownEntitiesById: { [key: string]: any }
    ): any {
        if (!_.isNil(row.zoneCombinationId)) {
            return zonesById[row.zoneCombinationId] || null;
        }

        if (!_.isNil(row.overlayId)) {
            return overlaysById[row.overlayId] || null;
        }

        return unknownEntitiesById[row.unknownEntityId] || null;
    }

    private takeAttributeById(
        attributesById: { [key: string]: ICalibrationSetTableFieldAttribute },
        attributeId: number | null | undefined
    ): ICalibrationSetTableFieldAttribute | null {
        if (_.isNil(attributeId)) {
            return null;
        }

        const key = String(attributeId);
        const attribute = attributesById[key] || null;
        if (attribute) {
            delete attributesById[key];
        }

        return attribute;
    }

    private getUnknownTableFieldsById(): {
        [key: string]: ITableField;
    } {
        return _.keyBy(
            _.get(
                this.validationData,
                'importValidationResult.calibrationSetTableFieldAttributesImport.unknownTableFields',
                []
            ) as ITableField[],
            field => String(field.Id)
        );
    }

    private resolveEntityType(row: ICalibrationSetTableFieldAttributesImportTableRow, entity: any): string {
        if (!_.isNil(row.zoneCombinationId)) {
            return 'Zone';
        }

        if (!_.isNil(row.overlayId)) {
            return _.get(entity, 'BuildingCalibrationType') === OverlayType.BUILDING_TYPOLOGY ? 'Typology' : 'Overlay';
        }

        return _.get(entity, 'Type', '');
    }
}
